/**
 * Resolves a `BarcodeDetector`, falling back to a WebAssembly polyfill where the browser has none.
 *
 * ⚠️ **Windows is the reason this exists** — not, any longer, the iPad. [Decision 0137] justified the
 * polyfill by iPadOS, and [decision 0140] then ruled Apple out entirely: the kiosk runs on a **Chromebook or
 * a Microsoft Surface**, in a browser. The polyfill survives that for a different reason, and it is worth
 * writing down because the obsolete one is still quoted: Chromium ships `BarcodeDetector` on **ChromeOS,
 * macOS and Android only** [MDN browser-compat-data; developer.chrome.com/docs/capabilities/shape-detection].
 * **Chrome and Edge on Windows have none.** So the Chromebook decodes with the browser's own API and the
 * Surface has no path but this polyfill — on one of the two form factors ISS named, removing it would leave
 * a scan that can never work, silently, because the screen degrades politely to "type it by hand".
 *
 * **Loaded lazily, and only where it is missing.** A Chromebook has the native API and must not pay for
 * 1.1 MB of WebAssembly it will never execute: this app boots on cheap hardware over branch-office Wi-Fi,
 * which is why the whole flow is deliberately not code-split. The polyfill is the one exception, and it is
 * fetched only after a candidate taps the camera on a device that needs it.
 *
 * **The binary is ours, not a CDN's.** `zxing-wasm` defaults `locateFile` to jsDelivr, which this
 * repository forbids — no third-party call — and which would fail anyway on a branch network that does not
 * reach it. The `?url` import below makes Vite emit the `.wasm` as a hashed asset of our own bundle, so the
 * only request the scanner ever makes is same-origin.
 */
/**
 * The formats a pay card's barcode is actually printed in.
 *
 * Narrow on purpose. Handing the detector every format it supports makes it slower and, worse, lets it
 * return a confident read of something else in frame — a badge, a shelf label, a poster on the wall behind
 * the candidate. A pay-card proxy ID is a linear code, so 2-D formats are not listed at all.
 *
 * It lives HERE, with the loader, for the same reason the detector's type does: the warm-up below has to
 * construct the detector exactly the way the scanner will. The ponyfill validates these names in its
 * CONSTRUCTOR and throws a `TypeError` on one it does not know, so a warm-up that constructed with no
 * options would leave that failure to happen later — after the camera is open, where the scanner cannot
 * report it. A `barcode-detector` major bump that renames a format is caught here, before anything opens.
 */
export const FORMATS = ['code_128', 'code_39', 'codabar', 'itf', 'ean_13'] as const;

export interface BarcodeDetectorLike {
  detect: (source: CanvasImageSource) => Promise<readonly { rawValue: string }[]>;
}

type DetectorCtor = new (options?: { formats?: readonly string[] }) => BarcodeDetectorLike;

/** Resolved once per page: the polyfill's module and its WebAssembly are worth loading a single time. */
let cached: Promise<DetectorCtor | null> | undefined;

export function loadBarcodeDetector(): Promise<DetectorCtor | null> {
  cached ??= resolve();
  return cached;
}

async function resolve(): Promise<DetectorCtor | null> {
  const native = (globalThis as { BarcodeDetector?: DetectorCtor }).BarcodeDetector;
  if (native !== undefined) return native;

  try {
    const [{ BarcodeDetector, prepareZXingModule }, { default: wasmUrl }] = await Promise.all([
      import('barcode-detector/ponyfill'),
      import('zxing-wasm/reader/zxing_reader.wasm?url'),
    ]);

    // Same-origin, from our own bundle. Without this the module reaches for jsDelivr.
    prepareZXingModule({ overrides: { locateFile: () => wasmUrl }, fireImmediately: false });

    const Polyfilled = BarcodeDetector as unknown as DetectorCtor;
    await warmUp(Polyfilled);
    return Polyfilled;
  } catch {
    // A polyfill that will not load is the same fact as no scanner: the screen says so and offers the
    // keyboard and a representative. Never a blank overlay [BR-K7].
    return null;
  }
}

/**
 * Compiles the WebAssembly HERE, where failing still means something.
 *
 * ⚠️ **Importing the module proves nothing.** The ES module imports fine and the constructor returns
 * happily; the `.wasm` is only compiled on the FIRST `detect()`. So a device where WebAssembly cannot run —
 * a Content-Security-Policy without `'wasm-unsafe-eval'`, a binary that did not deploy, a network that
 * dropped it — used to resolve a detector that was never going to decode anything. The hook then opened the
 * camera and threw inside its per-frame `catch`, which is written for *one bad frame*: a live camera, four
 * errors a second, forever. That is precisely the outcome this module's contract calls the worst of the
 * three — it looks like it is working.
 *
 * One `detect()` against a throwaway 1×1 canvas moves that failure inside `resolve`'s `try`, so it becomes
 * `null` — `unsupported` — "enter the ID by hand" / "ask a representative", the fallback KPC-1 already
 * specifies, reached BEFORE the camera is ever requested. The deliberate ordering the hook documents is
 * preserved: nobody is asked to allow a camera the device cannot decode with.
 *
 * **1×1 and blank on purpose.** The ponyfill short-circuits a source of zero width or height — it returns
 * `[]` without touching the WebAssembly, which would make this warm-up prove nothing at all. At 1×1 the
 * binary really is compiled and really is asked to read; ZXing tolerates the size and finds nothing
 * (verified against `zxing-wasm` 3.1.3 at 1×1, 2×2 and 8×8: `[]`, no throw), so the only way this rejects
 * is the way we want it to.
 *
 * **Constructed with the formats the scanner uses**, not with none: the ponyfill validates format names in
 * the constructor, so warming up with a different construction would prove less than it appears to.
 *
 * The native path never reaches here and pays nothing for it. And this resolves once per page, so a device
 * that fails here says "type it by hand" until the page is reloaded — deliberate, and the same answer the
 * polyfill's own module cache would give: `zxing-wasm` memoises a rejected instantiation too, so retrying
 * would need that purged as well.
 */
async function warmUp(Detector: DetectorCtor): Promise<void> {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;

  await new Detector({ formats: [...FORMATS] }).detect(canvas);
}
